import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// ============================================================
// TYPES
// ============================================================

export interface PlaceCandidate {
  id: string;
  source: string;
  source_id: string | null;
  source_url: string | null;
  name: string;
  address_text: string | null;
  municipality: string | null;
  county: string | null;
  landskap: string | null;
  lat: number | null;
  lng: number | null;
  tags: string[];
  website: string | null;
  phone: string | null;
  raw_json: Record<string, unknown>;
  confidence: number;
  status: "new" | "matched" | "needs_review" | "rejected" | "approved";
  matched_place_id: string | null;
  matched_place?: {
    id: string;
    name: string;
    slug: string;
  } | null;
  decided_by: string | null;
  decided_at: string | null;
  decision_notes: string | null;
  created_at: string;
  updated_at: string;
  image_url: string | null;
  image_thumb_url: string | null;
  image_source: string | null;
  image_license: string | null;
  image_attribution: string | null;
}

export interface PlaceIngestRun {
  id: string;
  source: string;
  trigger: "admin" | "cron";
  params: Record<string, unknown>;
  inserted_count: number;
  updated_count: number;
  status: "running" | "success" | "failed";
  error: string | null;
  started_at: string;
  finished_at: string | null;
  created_by: string | null;
}

export interface IngestRequest {
  source: "osm";
  mode: "bbox" | "nearby";
  bbox?: [number, number, number, number];
  center?: [number, number];
  radius_m?: number;
  limit?: number;
  tags?: string[];
}

export interface IngestResult {
  success: boolean;
  run_id: string;
  inserted_count: number;
  updated_count: number;
  total_fetched: number;
}

export interface MatchResult {
  success: boolean;
  processed: number;
  matched: number;
  needs_review: number;
}

// ============================================================
// HOOKS
// ============================================================

/**
 * Fetch place candidates with optional status filter
 */
export function usePlaceCandidates(status?: PlaceCandidate["status"] | "all") {
  return useQuery({
    queryKey: ["place-candidates", status],
    queryFn: async () => {
      let query = supabase
        .from("place_candidates")
        .select(`
          *,
          matched_place:places!matched_place_id (
            id,
            name,
            slug
          )
        `)
        .order("created_at", { ascending: false });

      if (status && status !== "all") {
        query = query.eq("status", status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as unknown as PlaceCandidate[];
    },
  });
}

/**
 * Fetch ingest run history
 */
export function usePlaceIngestRuns(limit = 20) {
  return useQuery({
    queryKey: ["place-ingest-runs", limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("place_ingest_runs")
        .select("*")
        .order("started_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as PlaceIngestRun[];
    },
  });
}

/**
 * Run places ingest via edge function
 */
export function useRunPlacesIngest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: IngestRequest): Promise<IngestResult> => {
      const { data, error } = await supabase.functions.invoke("admin-run-places-ingest", {
        body: request,
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || "Ingest failed");

      return data as IngestResult;
    },
    onSuccess: (data) => {
      toast.success(`Import klar: ${data.inserted_count} nya, ${data.updated_count} uppdaterade`);
      queryClient.invalidateQueries({ queryKey: ["place-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["place-ingest-runs"] });
    },
    onError: (error) => {
      toast.error(`Import misslyckades: ${error.message}`);
    },
  });
}

/**
 * Run places matching via edge function
 */
export function useRunPlacesMatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (batch?: number): Promise<MatchResult> => {
      const batchSize = batch ?? 50;
      const { data, error } = await supabase.functions.invoke("admin-run-places-match", {
        body: { batch: batchSize, status: "new" },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || "Matching failed");

      return data as MatchResult;
    },
    onSuccess: (data) => {
      toast.success(`Matchning klar: ${data.matched} matchade, ${data.needs_review} behöver granskning`);
      queryClient.invalidateQueries({ queryKey: ["place-candidates"] });
    },
    onError: (error) => {
      toast.error(`Matchning misslyckades: ${error.message}`);
    },
  });
}

/**
 * Create place from candidate
 */
export function useCreatePlaceFromCandidate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ candidateId, notes, descriptionShort }: { candidateId: string; notes?: string; descriptionShort?: string }) => {
      console.log("[create-place-from-candidate] start", { candidate_id: candidateId, has_description: !!descriptionShort?.trim() });

      const { data, error } = await supabase.rpc("create_place_from_candidate", {
        p_candidate_id: candidateId,
        p_notes: notes,
        p_description_short: descriptionShort?.trim() || null,
      });

      if (error) {
        console.error("[create-place-from-candidate] insert failed", { error: error.message });
        throw error;
      }
      console.log("[create-place-from-candidate] insert ok", { place_id: data });
      return data as string;
    },
    onSuccess: () => {
      toast.success("Ny plats skapad (utkast)");
      queryClient.invalidateQueries({ queryKey: ["place-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["admin-places"] });
    },
    onError: (error) => {
      toast.error(`Kunde inte skapa plats: ${error.message}`);
    },
  });
}

/**
 * Merge candidate to existing place
 */
export function useMergeCandidateToPlace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      candidateId,
      placeId,
      notes,
    }: {
      candidateId: string;
      placeId: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase.rpc("merge_candidate_to_place", {
        p_candidate_id: candidateId,
        p_place_id: placeId,
        p_notes: notes,
      });

      if (error) throw error;
      return data as string; // Returns place ID
    },
    onSuccess: () => {
      toast.success("Kandidat mergad till befintlig plats");
      queryClient.invalidateQueries({ queryKey: ["place-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["admin-places"] });
    },
    onError: (error) => {
      toast.error(`Kunde inte merga: ${error.message}`);
    },
  });
}

/**
 * Reject candidate
 */
export function useRejectCandidate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ candidateId, notes }: { candidateId: string; notes?: string }) => {
      const { data, error } = await supabase.rpc("reject_place_candidate", {
        p_candidate_id: candidateId,
        p_notes: notes,
      });

      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      toast.success("Kandidat avvisad");
      queryClient.invalidateQueries({ queryKey: ["place-candidates"] });
    },
    onError: (error) => {
      toast.error(`Kunde inte avvisa: ${error.message}`);
    },
  });
}

/**
 * Candidate counts by status
 */
export function useCandidateCounts() {
  return useQuery({
    queryKey: ["place-candidate-counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("place_candidates")
        .select("status");

      if (error) throw error;

      const counts = {
        new: 0,
        matched: 0,
        needs_review: 0,
        rejected: 0,
        approved: 0,
        total: 0,
      };

      for (const row of data ?? []) {
        counts[row.status as keyof typeof counts]++;
        counts.total++;
      }

      return counts;
    },
  });
}
